import Debug from 'debug';
import { useEffect, useState } from 'react';
import { getConfig, logout, NotifyInfo, store } from 'gossamer-front-end';

export enum AuthState {
  loading = 'LOADING',
  readyForSignIn = 'READY_FOR_SIGN_IN',
  confirmSignInWithTOTP = 'CONFIRM_SIGN_IN_WITH_TOTP',
  signedIn = 'SIGNED_IN',
  signUpRequested = 'SIGN_UP_REQUESTED',
  signUpInitiated = 'SIGN_UP_INITIATED',
  completeSignUpWithTOTP = 'COMPLETE_SIGN_UP_WITH_TOTP',
  passwordResetRequested = 'PASSWORD_RESET_REQUESTED',
  passwordResetInitiated = 'PASSWORD_RESET_INITIATED',
  forcedPasswordChange = 'FORCED_PASSWORD_CHANGE',
}

type CognitoUser = any;
type MFAChallenge = 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA';

export interface AuthFunctions {
  // completeNewPassword: (newPassword: string) => Promise<void>;
  // completePasswordReset: (code: string, password: string) => Promise<void>;
  getAuthState: () => AuthState;
  getCognitoUser: () => CognitoUser;
  getTempUserName: () => string | null;
  getTOTPSecret: () => string;
  // initiatePasswordReset: (userName: string) => Promise<void>;
  requestPasswordReset: () => void;
  requestSignUp: () => void;
  resendSignUpCode: () => Promise<void>;
  // resendPasswordResetCode: () => Promise<void>;
  returnToSignIn: () => void;
  // setEmailConfSettings: (userName: string, code: string) => void;
  // signIn: (userName: string, password: string) => Promise<void>;
  // signUp: (email: string, givenName: string, familyName: string, password: string) => Promise<void>;
  // supplyConfirmationCode: (code: string) => Promise<void>;
  // supplyTOTPCode: (challengeAnswer: string) => Promise<void>;
  userMessage: NotifyInfo;
  // verifyTOTPToken: (challengeAnswer: string) => Promise<void | string>;
}

const debug = Debug('gfe/au/useAuth');

// setup auto logout after N minutes of inactivity
export const setLogoutIfInactiveTimeoutMins = (logoutTimeoutMins: number, redirectUrl: string) => {
  let loginTimeout: number | undefined = undefined;
  const onTimeout = () => {
    window.localStorage.clear();
    window.location.href = redirectUrl;
  };
  const listenerFunc = () => {
    if (loginTimeout !== undefined) {
      clearTimeout(loginTimeout);
    }
    loginTimeout = window.setTimeout(onTimeout, logoutTimeoutMins * 60000);
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('click', listenerFunc);
    window.addEventListener('keydown', listenerFunc);
  }
  listenerFunc();
};

// avoid registering multiple Hub listeners
let authEventListener: (data: any) => void = () => {}; // eslint-disable-line
// Hub.listen("auth", (data) => {
//   authEventListener(data);
// });

const DEFAULT_MESSAGE: NotifyInfo = ['Please enter your login details', 'primary'];

const identity = (x: string) => x;

export const useAuth = (
  mapCognitoIdToUserId: (value: string) => string,
  mapCognitoIdToRoleId: (value: string) => string,
): AuthFunctions => {
  const [status, setStatus] = useState<AuthState>(AuthState.loading);
  const [userMessage, setUserMessage] = useState<NotifyInfo>(DEFAULT_MESSAGE);
  const [currCognitoUser, setCurrCognitoUser] = useState<CognitoUser>(null);
  const [newCognitoUser, setNewCognitoUser] = useState<CognitoUser>(null);
  const [tempConfCode, setTempConfCode] = useState<string | null>(null);
  const [tempUserName, setTempUserName] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [TOTPSecret, setTOTPSecret] = useState<string | null>(null);
  const [currChallenge, setCurrChallenge] = useState<MFAChallenge | null>(null);

  const updateConfigOnSuccessfulSignIn = () => {
    debug('updateConfigOnSuccessfulSignIn');
    // fetchAuthSession()
    //   .then((session) => {
    //     debug("current credentials", session);
    //     const idTokenPayload = session.tokens?.idToken?.payload || {};
    //     debug("id token payload:", idTokenPayload);
    //     // const groups = idTokenPayload["cognito:groups"] || [];
    //     const email = (idTokenPayload.email as string) || "no-email";
    //     const userId = (mapCognitoIdToUserId || identity)(session.identityId);
    //     const roleId = (mapCognitoIdToRoleId || identity)(session.identityId);
    //     const loginPayload = {
    //       userId,
    //       roleId,
    //       orgPath: null,
    //       email,
    //       name: deduceNameFromEmail(email), // TODO
    //       groups: null,
    //     };
    //     setConfig("user-id", userId);
    //     setConfig("role-id", roleId);
    //     debug("login payload:", loginPayload);
    //     store.dispatch(login(loginPayload));
    //   })
    //   .catch(console.error);
  };

  useEffect(() => {
    if (getConfig('login-bypass') === 'true') {
      setStatus(AuthState.signedIn);
    } else {
      // await load of AWS config
      // getCurrentUser()
      //   .then((user) => {
      //     setCurrCognitoUser(user);
      //     setStatus(AuthState.signedIn);
      //     updateConfigOnSuccessfulSignIn();
      //   })
      //   .catch((err) => {
      //     if (err.name === 'UserUnAuthenticatedException') {
      //       debug(`not authenticated`);
      //     } else {
      //       console.error(typeof err);
      //       console.error(err);
      //       console.error(JSON.stringify(err));
      //     }
      //     setStatus(AuthState.readyForSignIn);
      //   });
    }
  }, []);

  const reportSystemError = (error: any) => {
    console.error(error); // reports this to datadog
    setUserMessage(['An error occurred with authentication, please try again later', 'danger']);
    throw error instanceof Error ? error : new Error(error);
  };

  const reportUserError = (error: string) => {
    // no need to console.error() this out to datadog
    setUserMessage([error, 'danger']);
    throw new Error(error);
  };

  const resetError = () => {
    setUserMessage(DEFAULT_MESSAGE);
    // setErrorMessage("");
  };

  authEventListener = (data: any) => {
    const event = data.payload?.event || 'unknown';
    if (event === 'signOut') {
      store.dispatch(logout({}));
      setStatus(AuthState.readyForSignIn);
    }
  };

  const completeNewPassword = (newPassword: string) => {
    if (status !== AuthState.forcedPasswordChange) {
      throw new Error(`invalid AuthState: ${status}`);
    }
    // return confirmSignIn({ challengeResponse: newPassword })
    //   .then(signInResolved)
    //   .catch((error) => {
    //     reportSystemError(error);
    //   });
  };

  const completePasswordReset = (code: string, password: string) => {
    if (status !== AuthState.passwordResetInitiated) {
      throw new Error(`invalid AuthState: ${status}`);
    }
    if (!tempUserName) {
      throw new Error(`initiatePasswordReset not called`);
    }

    // return confirmResetPassword({ username: tempUserName, confirmationCode: code, newPassword: password })
    //   .then(() => {
    //     setStatus(AuthState.readyForSignIn);
    //     setTempUserName(null);
    //   })
    //   .catch((error) => {
    //     reportSystemError(error);
    //   });
  };

  const getAuthState = () => {
    return status;
  };

  const getCognitoUser = () => {
    return currCognitoUser;
  };

  const getTempUserName = () => {
    return tempUserName;
  };

  const getTOTPSecret = () => {
    if (!TOTPSecret) {
      throw new Error(`setupTOTP not called`);
    }
    return TOTPSecret;
  };

  const initiatePasswordReset = (userName: string) => {
    if (status !== AuthState.passwordResetRequested) {
      throw new Error(`invalid AuthState: ${status}`);
    }
    // return resetPassword({ username: userName })
    //   .then((output) => {
    //     const { nextStep } = output;
    //     switch (nextStep.resetPasswordStep) {
    //       case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
    //         const codeDeliveryDetails = nextStep.codeDeliveryDetails;
    //         setStatus(AuthState.passwordResetInitiated);
    //         setUserMessage([
    //           `A reset code has been sent to ${codeDeliveryDetails.deliveryMedium}, please enter it here`,
    //           'primary',
    //         ]);
    //         setTempUserName(userName);
    //         break;
    //       case 'DONE':
    //         setStatus(AuthState.readyForSignIn);
    //         setUserMessage([`Your password has been reset`, 'primary']);
    //         break;
    //     }
    //   })
    //   .catch((error) => {
    //     if (error.code === 'UserNotFoundException') {
    //       reportUserError('No user identified with this email address. Please check and try again');
    //     } else if (
    //       error.code === 'InvalidParameterException' &&
    //       error.message.includes('no registered/verified email or phone_number')
    //     ) {
    //       resendSignUpCode();
    //       setStatus(AuthState.signUpInitiated);
    //     } else {
    //       reportSystemError(error);
    //     }
    //   });
  };

  const requestPasswordReset = () => {
    if (status !== AuthState.readyForSignIn) {
      throw new Error(`invalid AuthState: ${status}`);
    }
    resetError();
    setStatus(AuthState.passwordResetRequested);
    setUserMessage(['Enter your email address below to reset your password', 'primary']);
  };

  const requestSignUp = () => {
    if (status !== AuthState.readyForSignIn) {
      throw new Error(`invalid AuthState: ${status}`);
    }
    resetError();
    setStatus(AuthState.signUpRequested);
  };

  const resendPasswordResetCode = () => {
    if (status !== AuthState.passwordResetInitiated) {
      throw new Error(`invalid AuthState: ${status}`);
    }
    if (!tempUserName) {
      throw new Error(`signUp not called`);
    }
    // return resetPassword({ username: tempUserName })
    //   .then(() => {})
    //   .catch((error) => reportSystemError(error));
  };

  const resendSignUpCode = () => {
    if (status !== AuthState.signUpInitiated) {
      throw new Error(`invalid AuthState: ${status}`);
    }
    if (!tempUserName) {
      throw new Error(`signUp not called`);
    }
    return resendSignUpCode();
  };

  const returnToSignIn = () => {
    if (status === AuthState.signedIn) {
      throw new Error(`invalid AuthState: ${status}`);
    }
    resetError();
    setStatus(AuthState.readyForSignIn);
  };

  const revertMFAtoTOTP = () => {
    setTempUserName(newCognitoUser.username);
    // return updateMFAPreference({ totp: 'PREFERRED' }).then(() => {
    //   setStatus(AuthState.completeSignUpWithTOTP);
    // });
  };

  const signIn = async (username: string, password: string, recursed?: boolean) => {
    if (status !== AuthState.readyForSignIn && status !== AuthState.signUpInitiated) {
      throw new Error(`invalid AuthState: ${status}`);
    }
    debug(`signIn(${username}, ${password})`);

    // return signInAmplify({ username, password })
    //   .then(signInResolved)
    //   .catch((error) => {
    //     debug(error);
    //     ['UserNotFoundException', 'NotAuthorizedException'].indexOf(error.code) > -1
    //       ? reportUserError('The username and/or password entered were incorrect')
    //       : reportSystemError(error);
    //   });
  };

  // const signInResolved = (output: SignInOutput) => {
  //   resetError();
  //   const { nextStep } = output;
  //   debug(`signInResolved`, nextStep.signInStep);
  //   switch (nextStep.signInStep) {
  //     // ...
  //     case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
  //       const totpSetupDetails = nextStep.totpSetupDetails;
  //       const appName = 'my_app_name';
  //       const setupUri = totpSetupDetails.getSetupUri(appName);
  //       // Open setupUri with an authenticator APP to retrieve an OTP code
  //       break;
  //     // ...
  //     case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
  //     case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
  //       setStatus(AuthState.confirmSignInWithTOTP);
  //       setUserMessage(['Please enter your MFA code', 'primary']);
  //       break;
  //     case 'DONE':
  //       setTempPassword(null);
  //       getCurrentUser()
  //         .then((user) => {
  //           setCurrCognitoUser(user);
  //           setStatus(AuthState.signedIn);
  //           updateConfigOnSuccessfulSignIn();
  //         })
  //         .catch(console.error);
  //   }
  // };

  const signUp = (email: string, givenName: string, familyName: string, password: string, phoneNumber?: string) => {
    if (status !== AuthState.signUpRequested) {
      throw new Error(`invalid AuthState: ${status}`);
    }

    // return signUpAmplify({
    //   username: email,
    //   password,
    //   options: {
    //     userAttributes: {
    //       email,
    //       given_name: givenName,
    //       family_name: familyName,
    //       'custom:sign_up_phone_nbr': phoneNumber,
    //     },
    //     clientMetadata: {
    //       siteUrl: `${window.location.href}`, // + withPrefix("")
    //     },
    //   },
    // })
    //   .then(() => {
    //     resetError();
    //     setTempUserName(email);
    //     setTempPassword(password);
    //     setStatus(AuthState.signUpInitiated);
    //   })
    //   .catch((error) => {
    //     if (error.message.toLowerCase().includes('a federated user with this email address already exists')) {
    //       // nb, custom error message here, as error comes back from lambda as "PreSignUp responded with error ...", which looks a bit rubbish when shown in UI
    //       reportUserError(
    //         'An account already exists with this email address, created using an external account e.g. Google'
    //       );
    //       return;
    //     }
    //     reportUserError(error.message);
    //   });
  };

  const supplyConfirmationCode = (code: string) => {
    if (!tempUserName) {
      throw new Error(`signUp not called`);
    }
    if (status !== AuthState.readyForSignIn && status !== AuthState.signUpInitiated) {
      throw new Error(`invalid AuthState: ${status}`);
    }

    // return confirmSignUp({ username: tempUserName, confirmationCode: code })
    //   .then(() => {
    //     if (tempPassword) {
    //       return signIn(tempUserName, tempPassword);
    //     }
    //     setStatus(AuthState.readyForSignIn);
    //     setTempUserName(null);
    //   })
    //   .catch((error) => reportSystemError(error));
  };

  const supplyTOTPCode = (challengeResponse: string) => {
    // if (!newCognitoUser) {
    //   throw new Error(`newCognitoUser not set yet`);
    // }
    // if (!currChallenge) {
    //   throw new Error(`currChallenge not set`);
    // }
    if (status !== AuthState.confirmSignInWithTOTP) {
      throw new Error(`invalid AuthState: ${status}`);
    }
    // return confirmSignIn({ challengeResponse })
    //   .then(() => {
    //     resetError();
    //     setCurrChallenge(null);
    //     // setCurrCognitoUser(newCognitoUser);
    //     if (currChallenge === 'SMS_MFA') {
    //       return revertMFAtoTOTP();
    //     }
    //     updateConfigOnSuccessfulSignIn();
    //     // window.location.reload(); // hack to get menu to update properly
    //     setStatus(AuthState.signedIn);
    //   })
    //   .catch((error) => {
    //     console.error(error);
    //     reportUserError(
    //       error.code === 'ExpiredCodeException'
    //         ? 'The code entered has already been used, please wait until there is a new one'
    //         : 'The code entered was incorrect'
    //     );
    //   });
  };

  return {
    // completeNewPassword,
    // completePasswordReset,
    getAuthState,
    getCognitoUser,
    getTempUserName,
    getTOTPSecret,
    // initiatePasswordReset,
    requestPasswordReset,
    requestSignUp,
    // resendPasswordResetCode,
    resendSignUpCode,
    returnToSignIn,
    // signIn,
    // signUp,
    // supplyConfirmationCode,
    // supplyTOTPCode,
    userMessage,
  };
};
